import { PersistedData } from "../core/persisted-data"
import {
  BUMPER_PROGRESSION,
  BUMPER_SPAWN_INTERVAL,
  GAME_HEIGHT,
  GAME_WIDTH,
  SLINGSHOT_SPAWN_INTERVAL,
} from "../data/constants"
import {
  getEnemyPopulationCap,
  getSectorForWave,
  getWaveBatchSize,
  isBossWave,
  rollEnemyTypeForWave,
} from "../data/sectors"
import type { BumperEffectType } from "../types"

const MIN_SPAWN_Y = GAME_HEIGHT * 0.15
const MAX_SPAWN_Y = GAME_HEIGHT * 0.85

class WaveStrategy {
  getEnemyDifficultyMultiplier(wave: number): number {
    const sector = getSectorForWave(wave)
    const sectorPressure =
      1 + Math.max(0, wave - sector.waveStart) * 0.035 + (wave - 1) * 0.018
    const dangerMult = 1 + PersistedData.getDangerLevel() * 0.15
    return sector.enemyDensityMultiplier * sectorPressure * dangerMult
  }

  getSpawnTempoMultiplier(_wave: number, _waveProgress: number): number {
    return 1
  }

  getSpawnBatchSize(wave: number): number {
    return getWaveBatchSize(wave)
  }

  getEnemyPopulationCap(wave: number): number {
    return getEnemyPopulationCap(wave)
  }

  getEncounterSize(wave: number, maxAvailable: number): number {
    if (wave <= 2) return 1
    const waveCap = isBossWave(wave) ? 2 : 3
    return Math.min(waveCap, maxAvailable)
  }

  shouldUseEncounter(wave: number, spawnCycle: number): boolean {
    return wave >= 3 && spawnCycle > 0 && spawnCycle % 4 === 0
  }

  getSpawnPoints(
    count: number,
    _wave: number,
    _waveProgress: number,
  ): { x: number; y: number }[] {
    const points: { x: number; y: number }[] = []
    for (let i = 0; i < count; i++) {
      points.push({
        x: GAME_WIDTH + 60 + i * 50,
        y: MIN_SPAWN_Y + Math.random() * (MAX_SPAWN_Y - MIN_SPAWN_Y),
      })
    }
    return points
  }

  getBumperSpawnIntervalMs(wave: number): number {
    const sector = getSectorForWave(wave)
    return BUMPER_SPAWN_INTERVAL * sector.bumperDensityMultiplier
  }

  getBumperTypeWeights(wave: number): Record<BumperEffectType, number> {
    const base: Record<BumperEffectType, number> = {
      charge: 1,
      guard: 0,
      bank: 0.1,
      heal: 0.1,
      levelup: 0.2,
    }

    if (wave >= BUMPER_PROGRESSION.levelupWave) base.levelup = 1.2
    if (wave >= BUMPER_PROGRESSION.healWave) base.heal = 0.8
    if (wave >= BUMPER_PROGRESSION.bankWave) base.bank = 0.8

    return base
  }

  getSlingshotSpawnIntervalMs(wave: number): number {
    const sector = getSectorForWave(wave)
    return SLINGSHOT_SPAWN_INTERVAL / sector.enemyDensityMultiplier
  }

  rollEnemyType(
    wave: number,
    waveProgress = 0.5,
  ): import("../types").EnemyType {
    return rollEnemyTypeForWave(wave, waveProgress)
  }
}

export const waveStrategy = new WaveStrategy()
